import React, { useMemo } from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  AssetHealthStatusIndicator,
  AssetActionsBar,
  SensorsNewIndicator,
} from '@/components/pages/AssetsPreviewPage';
import { useAppFilters, useQueryNavigation } from '@/hooks/shared';
import { Separator } from '@radix-ui/react-separator';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Asset } from '@/types/shared/asset.types';
import { ASSET_STUDIO, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import { useParams } from 'react-router-dom';
import { SENSORS_DATA_TYPE } from '@/const';
import { useDispatch } from 'react-redux';
import { setSelectedComponentObj } from '@/store/slices/assetStudioSlice';
import { HEALTH_STATUS } from '@/types';
import { sortComponentsByHealthStatusAndName } from '@/components/pages/AssetsStudioPage/SidePane/ComponentSelector/util';
import { healthStatusFlagsToString } from '@/utils/shared';
interface AssetsDropListProps {
  asset: Asset;
  assets: Asset[];
  siteId: string;
}
// @TODO: make the dropdown a reusable component
// @TODO: same item as the component card(?)
// @TODO: create a util function that navigate to diagnostics and clears the object
export const AssetsDropdown = ({ asset, assets, siteId }: AssetsDropListProps) => {
  const { asset_ui_name, asset_supplier, asset_model } = asset || {};
  const dispatch = useDispatch();
  const { assetStudio, setFilters, assetsType } = useAppFilters();
  const { id } = useParams();
  const { navigateWithQuery } = useQueryNavigation();
  const [isOpen, setIsOpen] = useState<boolean>();

  return (
    <>
      <Popover
        onOpenChange={(open) => {
          setIsOpen(open);
        }}
      >
        <PopoverTrigger
          className={cn(
            'flex items-center gap-2 ml-[-0.5rem] px-2 py-1',
            `${isOpen ? 'bg-primary-600 text-white rounded-lg' : ''}`
          )}
        >
          {`${asset_ui_name} ${asset_supplier ? ' - ' + asset_supplier + ' ' + asset_model : ''}`}
          {/* <StatusTag
            status={asset?.health_status}
            suffix={
              asset?.latest_diagnostic_created_at && format(new Date(asset?.latest_diagnostic_created_at), 'dd.MM.yyyy')
            }
          /> */}
          <ChevronDown size={'16px'} className={`${isOpen ? 'rotate-[180deg]' : ''}`} />
        </PopoverTrigger>
        <PopoverContent className="p-0 max-h-[500px] overflow-y-auto min-w-fit">
          {/* {filteredAssets?.map((el: Asset) => ( */}
          {sortComponentsByHealthStatusAndName(
            assets
              // filter per active asset
              ?.filter((el) => el.asset_category?.toLocaleLowerCase() === assetsType?.toLowerCase()?.split('-')[0])
          ).map((el: any) => (
            <React.Fragment key={el.asset_id}>
              <div
                className={cn(
                  'flex justify-between p-3 py-2.5 hover:bg-neutral-25 hover:cursor-pointer border-b-2 border-transparent',
                  `${
                    el?.not_monitored || el.health_status === HEALTH_STATUS.NOT_AVAILABLE
                      ? 'bg-neutral-25 ring-2 ring-inset ring-white'
                      : ''
                  }`,
                  `${el?.asset_id === id ? 'border-b-2 border-primary-600 ' : ''}`
                )}
                onClick={() => {
                  const { camera_components, asset_id } = el;
                  if (assetStudio === ASSET_STUDIO.VISUAL_AI) {
                    if (camera_components?.length) {
                      navigateWithQuery(`/sites/${siteId}/machines/${asset_id}/components`, {
                        replaceParams: {
                          [FILTER_PARAMS.SELECTED_COMPONENT]: camera_components[0].component_id,
                        },
                      });
                    }
                    // redirect to diagnostics tab if visual ai is active and asset doesn't have camera
                    // setSelectedComponentObj to clear selected object data
                    else {
                      navigateWithQuery(`/sites/${siteId}/machines/${el?.asset_id}/components`, {
                        replaceParams: {
                          [FILTER_PARAMS.SELECTED_COMPONENT]: ASSET_STUDIO.ALL_COMPONENTS,
                          [FILTER_PARAMS.ASSET_STUDIO]: ASSET_STUDIO.DIAGNOSTICS,
                        },
                      });
                    }
                  } else {
                    navigateWithQuery(`/sites/${siteId}/machines/${el?.asset_id}/components`, {
                      replaceParams: { [FILTER_PARAMS.SELECTED_COMPONENT]: ASSET_STUDIO.ALL_COMPONENTS },
                    });
                  }
                }}
              >
                <div
                  className={cn(
                    ' flex flex-col gap-1',
                    `${asset?.asset_id === el?.asset_id ? 'font-medium border-primary-600' : ''}`
                  )}
                >
                  <div className="whitespace-nowrap">{`${el.asset_ui_name} ${
                    el.asset_supplier ? ' - ' + el.asset_supplier + ' ' + el.asset_model : ''
                  }`}</div>
                  <AssetHealthStatusIndicator
                    healthStatus={el?.health_status}
                    counterOnly={true}
                    showReasons={true}
                    isMonitored={el?.not_monitored}
                    size={'sm'}
                    reasons={[...healthStatusFlagsToString(el), ...el.reasons_for_na]}
                  />
                </div>
                <AssetActionsBar
                  asset={el}
                  variant="component-card"
                  onClick={() => {
                    navigateWithQuery(`/sites/${siteId}/machines/${el?.asset_id}/components`);
                  }}
                />
              </div>
              <Separator className="bg-neutral-100 h-[1px]" />
            </React.Fragment>
          ))}
        </PopoverContent>
      </Popover>
      <SensorsNewIndicator asset={asset} />
    </>
  );
};
